import React from "react";
import { Panel, Table, Loader, Icon, Pagination } from "@culturehq/components";
import * as CHQ from "chq";

import analytics from "images/analytics.svg";

import { useGet, usePagination } from "app/hooks";
import { ProfileLink, StoryLink } from "app/links";
import { formatThousand, t, Trans } from "app/utils";
import { DateFormat } from "components/common/Time";
import UserThumbnail from "components/person/UserThumbnail";
import { Params } from "@culturehq/client";

type StoryAnalyticsProps = {
  storyAnalytics: CHQ.StoryAnalytics;
};

const StoryAnalytics: React.FC<StoryAnalyticsProps> = ({ storyAnalytics }) => (
  <tr>
    <td className="max-width-300 StoryAnalyticsInfo--Prompt">
      <StoryLink story={storyAnalytics.story} tabIndex={0}>
        <p className="StoryAnalyticsInfo--Prompt__Info">{storyAnalytics.story.question.question}</p>
      </StoryLink>
      <p className="StoryAnalyticsInfo--Prompt__Info-Date">
        <Trans>Published</Trans>
        {" "}
        <DateFormat time={storyAnalytics.story.createdAt} format="%m/%d/%y" />
      </p>
    </td>
    <td>
      <div className="StoryAnalyticsInfo--User">
        <ProfileLink user={storyAnalytics.story.creator}>
          <UserThumbnail user={storyAnalytics.story.creator} />
          <div>
            <p className="StoryAnalyticsInfo--User__Info">{storyAnalytics.story.creator.name}</p>
            <p className="StoryAnalyticsInfo--User__Info StoryAnalyticsInfo--User__Info-Title">{storyAnalytics.story.creator.title}</p>
          </div>
        </ProfileLink>
      </div>
    </td>
    <td>
      <div className="StoryAnalyticsInfo flx-row">
        <div className="StoryAnalyticsInfo--Data flx-row">
          <Icon icon="eye" />
          <span>{formatThousand(storyAnalytics.views)}</span>
        </div>
      </div>
    </td>
  </tr>
);

export type ListStoryAnalytics = {
  stories: CHQ.StoryAnalytics[];
  pagination: any;
};

const HighestPerformingStoriesContent: React.FC<{params: Params}> = ({ params }) => {
  const [page, onPageClick] = usePagination();
  const get = useGet<ListStoryAnalytics>(`/analytics/stories?page=${page}`, params);

  if (get.getting) {
    return <Loader loading />;
  }

  if (get.error) {
    throw new Error(t("Looks like there has been an unexpected error. Please reload the page."));
  }

  return (
    <>
      <Table className="table-responsive StoryAnalyticsTable">
        <thead>
          <tr>
            <th>
              <Trans>Story</Trans>
            </th>
            <th className="txt-center">
              <Trans>Author</Trans>
            </th>
            <th className="txt-center">
              <div className="StoryAnalyticsTable__LastHeading">
                <div className="StoryAnalyticsTable__LastHeadingContainer">
                  <Trans>Views</Trans>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {get.got.stories.length !== 0 && get.got.stories.map(storyAnalytics => (
            <StoryAnalytics
              key={storyAnalytics.story.id}
              storyAnalytics={storyAnalytics}
            />
          ))}
        </tbody>
      </Table>
      {get.got.stories.length === 0 && (
        <div className="StoryAnalyticsTable--Empty">
          <img className="StoryAnalyticsTable--Empty__Image" src={analytics} alt={t("Empty data")} />
          <h4 className="StoryAnalyticsTable--Empty__Label"><Trans>No data to display yet</Trans></h4>
        </div>
      )}
      <Pagination {...get.got.pagination} onClick={onPageClick} />
    </>
  );
};

const HighestPerformingStories: React.FC<{params: Params}> = ({ params }) => (
  <Panel>
    <Panel.Body className="card_holder">
      <div className="card_title">
        <h3>
          <Trans>Highest-Performing Stories</Trans>
        </h3>
      </div>
      <div className="card_info">
        <div className="w-100">
          <HighestPerformingStoriesContent params={params} />
        </div>
      </div>
    </Panel.Body>
  </Panel>
);

export default HighestPerformingStories;
